import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { Lock, Unlock, Save, AlertTriangle, UtensilsCrossed, Wine, GlassWater, Clock, Search, ChevronDown, ShieldAlert } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';

const GROUP_CONFIG = {
  kitchen: { label: 'Kitchen (Food)', icon: UtensilsCrossed, gradient: 'from-emerald-50 to-white', accent: '#2D6A4F' },
  bar: { label: 'Bar (Beverage)', icon: Wine, gradient: 'from-blue-50 to-white', accent: '#1D4ED8' },
};

// Convert split inputs to base qty for storage
function toBaseQty(category, split) {
  if (category === 'food') return (parseInt(split.kg) || 0) + (parseInt(split.grams) || 0) / 1000;
  if (category === 'beverage') return (parseInt(split.litres) || 0) + (parseInt(split.ml) || 0) / 1000;
  if (category === 'alcohol') {
    const bsml = split.bottle_size_ml || 750;
    return (parseInt(split.bottles) || 0) * bsml + (parseInt(split.pegs) || 0) * 30;
  }
  return 0;
}

// Convert base qty back to split values for display
function fromBaseQty(category, qty, bottleSizeMl) {
  if (category === 'food') {
    const kg = Math.floor(qty);
    const grams = Math.round((qty - kg) * 1000);
    return { kg: kg || '', grams: grams || '' };
  }
  if (category === 'beverage') {
    const litres = Math.floor(qty);
    const ml = Math.round((qty - litres) * 1000);
    return { litres: litres || '', ml: ml || '' };
  }
  if (category === 'alcohol') {
    const bsml = bottleSizeMl || 750;
    const totalMl = qty;
    const bottles = Math.floor(totalMl / bsml);
    const remainingMl = totalMl - bottles * bsml;
    const pegs = Math.round(remainingMl / 30);
    return { bottles: bottles || '', pegs: pegs || '' };
  }
  return {};
}

// ITER313 · Format previous day's closing as a human-readable, UOM-aware
// Opening Stock reference. Read-only display only — never feeds calc.
// Examples: '10 kg 500 g', '18 L 250 ml', '8 Bottles 5 Pegs', '850 ml',
// '75 Units'. Missing / zero qty → '—' (never defaults to 0 per spec).
function formatOpeningQty(category, qty, bottleSizeMl) {
  const q = Number(qty);
  if (!q || !Number.isFinite(q) || q <= 0) return '—';
  if (category === 'food') {
    const kg = Math.floor(q);
    const grams = Math.round((q - kg) * 1000);
    if (kg && grams) return `${kg} kg ${grams} g`;
    if (kg)          return `${kg} kg`;
    if (grams)       return `${grams} g`;
    return '—';
  }
  if (category === 'beverage') {
    const litres = Math.floor(q);
    const ml = Math.round((q - litres) * 1000);
    if (litres && ml) return `${litres} L ${ml} ml`;
    if (litres)       return `${litres} L`;
    if (ml)           return `${ml} ml`;
    return '—';
  }
  if (category === 'alcohol') {
    const bsml = bottleSizeMl || 750;
    const bottles = Math.floor(q / bsml);
    const remainingMl = q - bottles * bsml;
    const pegs = Math.round(remainingMl / 30);
    if (bottles && pegs) return `${bottles} Bottles ${pegs} Pegs`;
    if (bottles)         return `${bottles} Bottle${bottles === 1 ? '' : 's'}`;
    if (pegs)            return `${pegs} Peg${pegs === 1 ? '' : 's'}`;
    return '—';
  }
  // Fallback for future / unrecognised UOMs — display raw with the item's unit.
  return `${q} Units`;
}

export default function ManagerClosingStock({ categoryGroup = 'kitchen' }) {
  const { user, getHeaders, API, activeOutlet, canEditModule } = useAuth();
  const moduleWritable = canEditModule(categoryGroup);
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [consumption, setConsumption] = useState(null);
  const [splitValues, setSplitValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [earlyWarning, setEarlyWarning] = useState(false);
  const [pendingLock, setPendingLock] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState({ beverage: true, alcohol: false });
  const [closingWarnings, setClosingWarnings] = useState([]);
  const [overrideStatus, setOverrideStatus] = useState(null); // pending/approved/rejected/null
  const [overrideDialog, setOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [pinDialog, setPinDialog] = useState(false);
  const [pinValue, setPinValue] = useState('');
  const [pinError, setPinError] = useState('');
  const [pendingPinLock, setPendingPinLock] = useState(false);
  const [pinCorrected, setPinCorrected] = useState(false);
  const cfg = GROUP_CONFIG[categoryGroup];

  // Is this a past date (older than yesterday)?
  const yesterdayStr = format(new Date(Date.now() - 86400000), 'yyyy-MM-dd');
  const isPastDate = date < yesterdayStr;
  const overrideUnlockedPast = isPastDate && overrideStatus === 'approved';

  // Check previous closing status
  useEffect(() => {
    if (!date) return;
    axios.get(`${API}/inventory/closing/status?date=${date}`, { headers: getHeaders() })
      .then(res => setClosingWarnings(res.data.warnings || []))
      .catch(() => setClosingWarnings([]));
    // Load override request status for this date+section
    axios.get(`${API}/override-requests/mine`, { headers: getHeaders() })
      .then(res => {
        const req = res.data.find(r => r.date === date && r.module === 'inventory_closing' && r.section === categoryGroup);
        setOverrideStatus(req?.status || null);
      })
      .catch(() => setOverrideStatus(null));
  }, [date, API, getHeaders, categoryGroup]);

  const submitOverrideRequest = async () => {
    if (!overrideReason.trim()) { toast.error('Reason is mandatory'); return; }
    try {
      await axios.post(`${API}/override-requests`, {
        date, module: 'inventory_closing', section: categoryGroup, reason: overrideReason
      }, { headers: getHeaders() });
      toast.success('Override request submitted');
      setOverrideDialog(false);
      setOverrideStatus('pending');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/consumption?date=${date}`, { headers: getHeaders() });
      const data = res.data[categoryGroup];
      setConsumption(data);
      // Initialize split values from closing qty
      const splits = {};
      (data?.items || []).forEach(i => {
        splits[i.item_id] = fromBaseQty(i.category, i.closing_qty || 0, i.bottle_size_ml);
      });
      setSplitValues(splits);
    } catch { setConsumption(null); }
    finally { setLoading(false); }
  }, [date, categoryGroup, API, getHeaders]);

  useEffect(() => { load(); setPinCorrected(false); }, [load]);

  const handleSplitChange = (itemId, field, val) => {
    setSplitValues(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], [field]: val }
    }));
  };

  const buildClosingItems = () => {
    return (consumption?.items || []).map(item => {
      const split = splitValues[item.item_id] || {};
      const qty = toBaseQty(item.category, { ...split, bottle_size_ml: item.bottle_size_ml });
      return { item_id: item.item_id, qty };
    });
  };

  const handleSubmit = async (lock = false) => {
    // Check negative consumption — block manager
    const totalClosing = calcTotalClosing();
    const openingVal = consumption?.opening_value || 0;
    const issuesVal = consumption?.issues || 0;
    const calc = openingVal + issuesVal - totalClosing;
    if (calc < 0) {
      toast.error(`Closing value (${totalClosing.toFixed(0)}) exceeds Opening (${openingVal.toFixed(0)}) + Issues (${issuesVal.toFixed(0)}). Please verify entries.`);
      return;
    }

    // Early close warning ONLY for today's date before 11 PM
    const today = format(new Date(), 'yyyy-MM-dd');
    const hour = new Date().getHours();
    if (lock && date === today && hour < 23) {
      setEarlyWarning(true);
      setPendingLock(true);
      return;
    }
    await doSubmit(lock);
  };

  const doSubmit = async (lock = false, pin = '') => {
    setEarlyWarning(false);
    setSaving(true);
    try {
      const items = buildClosingItems();
      const payload = { date, category_group: categoryGroup, items };
      if (pin) payload.pin = pin;
      const res = await axios.post(`${API}/inventory/closing`, payload, { headers: getHeaders() });
      if (lock) {
        await axios.post(`${API}/inventory/closing/lock`, { date, category_group: categoryGroup }, { headers: getHeaders() });
      }
      // Show warnings from server
      if (res.data.warnings?.length > 0) {
        res.data.warnings.forEach(w => toast.warning(w));
      }
      toast.success(lock ? 'Closing stock submitted & locked' : 'Closing stock saved');
      load();
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || 'Failed to save';
      if (status === 428) {
        // Tier 2 — PIN required
        setPinDialog(true);
        setPinValue('');
        setPinError('');
        setPendingPinLock(lock);
      } else if (status === 409) {
        // Tier 3 — edit limit reached
        toast.info(msg, { duration: 6000 });
      } else if (status === 403 && msg.toLowerCase().includes('invalid pin')) {
        // Wrong PIN — re-open dialog
        setPinDialog(true);
        setPinValue('');
        setPinError('Invalid PIN — try again');
        setPendingPinLock(lock);
      } else {
        toast.error(msg);
      }
    }
    finally { setSaving(false); }
  };

  const handlePinConfirm = async () => {
    if (!pinValue || pinValue.length !== 4) {
      setPinError('Enter a 4-digit PIN');
      return;
    }
    // Verify PIN first via backend (with audit context)
    try {
      await axios.post(`${API}/auth/verify-pin`, {
        pin: pinValue,
        module: 'inventory_closing',
        entry_date: date,
        section: categoryGroup,
      }, { headers: getHeaders() });
    } catch (err) {
      setPinError(err.response?.data?.detail || 'Invalid PIN');
      return;
    }
    setPinError('');
    setPinDialog(false);
    setPinCorrected(true);
    await doSubmit(pendingPinLock, pinValue);
  };


  const items = consumption?.items || [];
  const locked = consumption?.locked || false;
  const editCount = consumption?.edit_count || 0;
  const Icon = cfg.icon;

  const foodItems = items.filter(i => i.category === 'food');
  const bevItems = items.filter(i => i.category === 'beverage');
  const alcItems = items.filter(i => i.category === 'alcohol');

  // Search filtering
  const filterBySearch = (list) => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(i => i.item_name.toLowerCase().includes(q));
  };
  const filteredFood = filterBySearch(foodItems);
  const filteredBev = filterBySearch(bevItems);
  const filteredAlc = filterBySearch(alcItems);

  // Auto-expand sections with search matches
  useEffect(() => {
    if (search.trim()) {
      const newExp = { ...expandedSections };
      if (filteredBev.length > 0) newExp.beverage = true;
      if (filteredAlc.length > 0) newExp.alcohol = true;
      setExpandedSections(newExp);
    }
  }, [search]);

  const toggleSection = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));

  // Calculate closing value for an item based on split inputs
  const getClosingValue = (item) => {
    const split = splitValues[item.item_id] || {};
    const qty = toBaseQty(item.category, { ...split, bottle_size_ml: item.bottle_size_ml });
    if (item.category === 'alcohol') {
      const bsml = item.bottle_size_ml || 750;
      return qty * (item.rate / bsml);
    }
    return qty * item.rate;
  };

  const renderSection = (sectionItems, title, sectionIcon, accordionKey = null) => {
    if (sectionItems.length === 0) return null;
    const SIcon = sectionIcon;
    const category = sectionItems[0]?.category;
    const isAccordion = !!accordionKey;
    const expanded = isAccordion ? expandedSections[accordionKey] : true;

    return (
      <div className="mb-5" key={title}>
        <button
          type="button"
          onClick={() => isAccordion && toggleSection(accordionKey)}
          className={`flex items-center gap-2 mb-2 w-full text-left ${isAccordion ? 'cursor-pointer hover:opacity-80' : ''}`}
          data-testid={`section-header-${accordionKey || category}`}
        >
          <SIcon className="w-4 h-4" style={{ color: cfg.accent }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[#5C6B64]">{title} ({sectionItems.length})</h3>
          {isAccordion && (
            <ChevronDown className={`w-3.5 h-3.5 text-[#5C6B64] transition-transform ${expanded ? 'rotate-0' : '-rotate-90'}`} />
          )}
        </button>
        {expanded && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] border-b border-[#E2E8DF]">
                <th className="px-3 py-2 text-left font-medium">Item</th>
                <th className="px-3 py-2 text-right font-medium">Rate</th>
                <th className="px-3 py-2 text-right font-medium">Opening Stock</th>
                {category === 'food' && (
                  <>
                    <th className="px-3 py-2 text-center font-medium">Kg</th>
                    <th className="px-3 py-2 text-center font-medium">Grams</th>
                  </>
                )}
                {category === 'beverage' && (
                  <>
                    <th className="px-3 py-2 text-center font-medium">Litres</th>
                    <th className="px-3 py-2 text-center font-medium">ML</th>
                  </>
                )}
                {category === 'alcohol' && (
                  <>
                    <th className="px-3 py-2 text-center font-medium">Bottles</th>
                    <th className="px-3 py-2 text-center font-medium">Pegs (30ml)</th>
                  </>
                )}
                <th className="px-3 py-2 text-right font-medium">Value</th>
              </tr>
            </thead>
            <tbody>
              {sectionItems.map(item => {
                const split = splitValues[item.item_id] || {};
                const closingVal = getClosingValue(item);

                return (
                  <tr key={item.item_id} className="border-b border-[#E2E8DF]/60" data-testid={`closing-row-${item.item_id}`}>
                    <td className="px-3 py-2 font-medium text-[#1A2421]">
                      <div>
                        {item.item_name}
                        {item.category === 'alcohol' && (
                          <span className="text-[10px] text-[#5C6B64] ml-1">({item.bottle_size_ml || 750}ml)</span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[#5C6B64] text-xs">{item.rate?.toFixed(2)}</td>
                    <td className="px-3 py-2 text-right text-[#5C6B64] text-xs whitespace-nowrap"
                        title="Previous day's closing stock — read-only reference"
                        data-testid={`opening-ref-${item.item_id}`}>
                      {formatOpeningQty(item.category, item.opening_qty, item.bottle_size_ml)}
                    </td>

                    {category === 'food' && (
                      <>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" step="1"
                            value={split.kg ?? ''} onChange={e => handleSplitChange(item.item_id, 'kg', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`kg-${item.item_id}`} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" max="999" step="1"
                            value={split.grams ?? ''} onChange={e => handleSplitChange(item.item_id, 'grams', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`grams-${item.item_id}`} />
                        </td>
                      </>
                    )}
                    {category === 'beverage' && (
                      <>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" step="1"
                            value={split.litres ?? ''} onChange={e => handleSplitChange(item.item_id, 'litres', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`litres-${item.item_id}`} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" max="999" step="1"
                            value={split.ml ?? ''} onChange={e => handleSplitChange(item.item_id, 'ml', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`ml-${item.item_id}`} />
                        </td>
                      </>
                    )}
                    {category === 'alcohol' && (
                      <>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" step="1"
                            value={split.bottles ?? ''} onChange={e => handleSplitChange(item.item_id, 'bottles', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`bottles-${item.item_id}`} />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <Input type="number" min="0" step="1"
                            value={split.pegs ?? ''} onChange={e => handleSplitChange(item.item_id, 'pegs', e.target.value)}
                            disabled={locked} placeholder="0"
                            className="w-16 h-8 text-center text-sm mx-auto bg-white border-[#E2E8DF]"
                            data-testid={`pegs-${item.item_id}`} />
                        </td>
                      </>
                    )}

                    <td className="px-3 py-2 text-right font-mono text-[#1A2421] text-xs">{closingVal.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}
      </div>
    );
  };

  // Calculate totals
  const calcTotalClosing = () => items.reduce((sum, item) => sum + getClosingValue(item), 0);

  return (
    <div className="space-y-4" data-testid={`closing-stock-${categoryGroup}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <Icon className="w-5 h-5" style={{ color: cfg.accent }} />
          <h1 className="text-xl font-bold text-[#1A2421]">{cfg.label} — Closing Stock</h1>
          {pinCorrected && (
            <Badge
              className="text-[10px] bg-[#D4A017]/10 text-[#B88718] border-[#D4A017]/30 font-semibold uppercase tracking-wide"
              data-testid="pin-corrected-tag"
              title="This date was unlocked via PIN correction. Values adjusted based on correction."
            >
              Corrected via PIN
            </Badge>
          )}
        </div>
        <Input type="date" value={date} onChange={e => setDate(e.target.value)}
          max={format(new Date(), 'yyyy-MM-dd')}
          className="w-[160px] h-8 text-sm bg-white border-[#E2E8DF]" data-testid="date-picker" />
      </div>

      {/* Previous closing warning */}
      {closingWarnings.length > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-testid="prev-closing-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <div>
            {closingWarnings.map((w, i) => (
              <p key={i} className="text-xs text-amber-700 font-medium">{w}</p>
            ))}
          </div>
        </div>
      )}

      <Card className={`border-[#E2E8DF] bg-gradient-to-b ${cfg.gradient}`}>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-[#1A2421] flex items-center gap-2">
              {locked ? <Lock className="w-4 h-4 text-red-500" /> : <Unlock className="w-4 h-4 text-emerald-600" />}
              {date}
              {activeOutlet && <Badge className="bg-[color:var(--color-primary)]/10 text-[color:var(--color-primary)] border-[color:var(--color-primary)]/20 text-[10px] ml-2">{activeOutlet.name || activeOutlet}</Badge>}
            </CardTitle>
            <div className="flex items-center gap-2">
              {consumption?.late_entry && (
                <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[10px]" data-testid="late-entry-badge">
                  <Clock className="w-3 h-3 mr-1" /> Late Entry
                </Badge>
              )}
              {editCount > 0 && (
                <Badge className={`text-[10px] ${editCount >= 3 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}
                  data-testid="edit-count-badge">
                  Edited {editCount}/3
                </Badge>
              )}
              {locked && <Badge className="bg-red-50 text-red-600 border-red-200 text-[10px]" data-testid="locked-badge">Locked</Badge>}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-[#5C6B64]" data-testid="no-items">
              <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-400" />
              <p className="text-sm">No items found. Ask your admin to set up inventory items.</p>
            </div>
          ) : (
            <>
              {/* Search */}
              <div className="flex items-center gap-2 mb-4 bg-white border border-[#E2E8DF] rounded-md px-3 py-2">
                <Search className="w-4 h-4 text-[#5C6B64]" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search items..." className="bg-transparent outline-none flex-1 text-sm text-[#1A2421]"
                  data-testid="item-search" />
                {search && <button onClick={() => setSearch('')} className="text-xs text-[#5C6B64] hover:text-[#1A2421]">Clear</button>}
              </div>

              {categoryGroup === 'kitchen' && renderSection(filteredFood, 'Food Items', UtensilsCrossed)}
              {categoryGroup === 'bar' && renderSection(filteredBev, 'Soft Beverages', GlassWater, 'beverage')}
              {categoryGroup === 'bar' && alcItems.length > 0 && renderSection(filteredAlc, 'Alcohol', Wine, 'alcohol')}

              {/* Summary */}
              {(() => {
                const totalClosing = calcTotalClosing();
                const openingVal = consumption?.opening_value || 0;
                const issuesVal = consumption?.issues || 0;
                const consumptionVal = openingVal + issuesVal - totalClosing;
                const isNegative = consumptionVal < 0;
                return (
                  <div className={`mt-4 p-3 rounded-lg border ${isNegative ? 'bg-red-50/80 border-red-200' : 'bg-white/80 border-[#E2E8DF]'}`}
                    data-testid="consumption-summary">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 text-center">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Opening</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold text-[#1A2421] truncate" title={openingVal.toLocaleString('en-IN')}>
                          {openingVal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Issues</p>
                        <p className="text-sm sm:text-base md:text-lg font-bold text-amber-600 truncate" title={issuesVal.toLocaleString('en-IN')}>
                          {issuesVal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Closing</p>
                        <p className={`text-sm sm:text-base md:text-lg font-bold truncate ${isNegative ? 'text-red-600' : 'text-[#1A2421]'}`} title={totalClosing.toLocaleString('en-IN')}>
                          {totalClosing.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Consumption</p>
                        <p className={`text-sm sm:text-base md:text-lg font-bold truncate ${isNegative ? 'text-red-600' : ''}`} style={isNegative ? {} : { color: cfg.accent }}
                          title={consumptionVal.toLocaleString('en-IN')}
                          data-testid="consumption-value">
                          {consumptionVal.toLocaleString('en-IN', { minimumFractionDigits: 0 })}
                        </p>
                      </div>
                    </div>
                    {isNegative && (
                      <div className="mt-2 flex items-center justify-center gap-1.5 text-xs text-red-600 font-medium" data-testid="negative-warning">
                        <AlertTriangle className="w-3.5 h-3.5" />
                        Closing stock cannot exceed Opening + Issues. Please verify your entries.
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Action area: always show override controls when non-editable */}
              {(() => {
                // View Only mode for supervisors without module access
                if (!moduleWritable) {
                  return (
                    <div className="mt-4 pt-3 border-t border-[#E2E8DF]">
                      <div className="flex items-center gap-2 px-3 py-2 bg-[#F3F5F2] rounded-lg" data-testid="view-only-closing">
                        <Lock className="w-3.5 h-3.5 text-[#5C6B64]" />
                        <span className="text-xs text-[#5C6B64] font-medium">View Only Access</span>
                        <span className="text-[10px] text-[#5C6B64] ml-auto">Module not assigned to you</span>
                      </div>
                    </div>
                  );
                }

                // Override approved for past date — PIN-based editing
                if (overrideUnlockedPast) {
                  return (
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" data-testid="override-approved-badge">
                          Override Approved
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="override-pin-prompt">
                        <ShieldAlert className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs font-medium text-emerald-700">Override approved — enter PIN to edit</p>
                          {!user?.pin_set && <p className="text-[10px] text-red-600 mt-0.5">PIN not set. Contact admin to set your PIN.</p>}
                        </div>
                        {user?.pin_set && (
                          <Button size="sm" onClick={() => { setPinDialog(true); setPinValue(''); setPinError(''); setPendingPinLock(false); }}
                            disabled={saving}
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            data-testid="pin-edit-closing-btn">
                            <Save className="w-3 h-3 mr-1" /> Edit with PIN
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                }

                const canSubmit = !locked && editCount < 3;
                const isBlocked = locked || editCount >= 3;
                const showOverrideBtn = isBlocked && !overrideStatus;
                return (
                  <div className="space-y-2 mt-4">
                    {/* Edit counter (when not locked and has edits) */}
                    {!locked && editCount > 0 && (
                      <div className="flex items-center justify-between px-1" data-testid="edit-counter">
                        <span className="text-[10px] text-[#5C6B64]">Edits used: {editCount} / 3</span>
                        <span className={`text-[10px] font-medium ${3 - editCount > 0 ? 'text-[#2D6A4F]' : 'text-[#C45A41]'}`}>
                          {3 - editCount > 0 ? `${3 - editCount} remaining` : 'No edits remaining'}
                        </span>
                      </div>
                    )}

                    {/* Override controls — visible for ANY non-editable state */}
                    {(showOverrideBtn || overrideStatus) && (
                      <div className="flex items-center gap-2" data-testid="override-controls">
                        {showOverrideBtn && (
                          <Button variant="outline" size="sm" onClick={() => { setOverrideDialog(true); setOverrideReason(''); }}
                            className="border-amber-300 text-amber-600 hover:bg-amber-50 text-xs"
                            data-testid="request-override-btn">
                            <ShieldAlert className="w-3.5 h-3.5 mr-1" /> Request Override
                          </Button>
                        )}
                        {overrideStatus === 'pending' && (
                          <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[9px]" data-testid="override-pending-badge">
                            <Clock className="w-2.5 h-2.5 mr-0.5" /> Override Requested
                          </Badge>
                        )}
                        {overrideStatus === 'approved' && !isPastDate && (
                          <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" data-testid="override-approved-badge">
                            Override Approved
                          </Badge>
                        )}
                        {overrideStatus === 'rejected' && (
                          <Badge className="bg-red-50 text-red-600 border-red-200 text-[9px]" data-testid="override-rejected-badge">
                            Override Rejected
                          </Badge>
                        )}
                      </div>
                    )}

                    {/* Submit buttons (only when can submit) */}
                    {canSubmit && (
                      <div className="flex items-center justify-end gap-2">
                        <Button variant="outline" onClick={() => handleSubmit(false)} disabled={saving}
                          className="border-[#E2E8DF] text-[#5C6B64] text-xs" data-testid="save-draft-btn">
                          <Save className="w-3.5 h-3.5 mr-1" /> {editCount > 0 ? `Save Edit (${editCount + 1}/3)` : 'Save Draft'}
                        </Button>
                        <Button onClick={() => handleSubmit(true)} disabled={saving}
                          className="text-white text-xs" style={{ backgroundColor: cfg.accent }} data-testid="submit-lock-btn">
                          <Lock className="w-3.5 h-3.5 mr-1" /> Submit & Lock
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </CardContent>
      </Card>

      {/* Early close warning */}
      <AlertDialog open={earlyWarning} onOpenChange={setEarlyWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" /> Early Closing
            </AlertDialogTitle>
            <AlertDialogDescription>
              Operations may still be ongoing. Are you sure you want to close and lock this entry now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setEarlyWarning(false); setPendingLock(false); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doSubmit(pendingLock)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="confirm-early-close">
              Yes, Close Now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Override Request Dialog */}
      <AlertDialog open={overrideDialog} onOpenChange={setOverrideDialog}>
        <AlertDialogContent className="border-[#E2E8DF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" /> Request Admin Override
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64] text-sm">
              Request admin to unlock {cfg.label} closing for {date}. Max edits reached — your reason will be reviewed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-[#5C6B64]">Reason (mandatory)</Label>
            <textarea value={overrideReason} onChange={e => setOverrideReason(e.target.value)}
              placeholder="Why does this entry need to be re-opened..."
              className="w-full mt-1 p-2 border border-[#E2E8DF] rounded-md text-sm min-h-[80px] focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              data-testid="override-reason-input" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={submitOverrideRequest}
              className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-override-btn">
              Submit Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PIN Verification Dialog (for override-approved past-date edits) */}
      <AlertDialog open={pinDialog} onOpenChange={open => { if (!open) setPinDialog(false); }}>
        <AlertDialogContent className="border-[#E2E8DF]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2421] flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-emerald-600" /> Enter PIN to Edit
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B64] text-sm">
              Override approved for {cfg.label} closing on {date}.
              Enter your 4-digit PIN to proceed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Label className="text-xs text-[#5C6B64]">4-digit PIN</Label>
            <Input
              data-testid="pin-input"
              type="password"
              inputMode="numeric"
              maxLength={4}
              placeholder="----"
              value={pinValue}
              onChange={e => { setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
              className="mt-1 h-12 text-center text-2xl tracking-[0.5em] font-mono border-[#E2E8DF] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
            />
            {pinError && <p className="text-xs text-red-600 mt-1" data-testid="pin-error">{pinError}</p>}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E2E8DF] text-[#5C6B64]" data-testid="pin-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePinConfirm}
              disabled={pinValue.length !== 4}
              className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="pin-confirm">
              Verify & Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
